import { Quote, QuoteDetails, QuoteItem, QuoteSection, QuoteStatus } from '../types';
import { parseDecimalInput } from './money';
import { calculateQuoteTotals, round2 } from './quoteLogic';
import { classifyTenderLvRow, parseTenderQuantity } from './tenderLvImport';

/**
 * Einlesen eines FERTIGEN Angebotsbelegs (eigenes Altangebot oder eines aus
 * einem Vorsystem) in einen Angebotsdatensatz.
 *
 * Abgrenzung zum LV-Import (tenderLvImport): dort ist das Dokument die
 * Ausschreibung des Auftraggebers, Preise werden bewusst NICHT gelesen. Hier
 * ist das Dokument das eigene Angebot — die Preise stehen drin und sind der
 * Grund des Imports.
 *
 * Zwei Festlegungen, die nicht verhandelbar sind:
 *  1. Die Summen des angelegten Angebots entstehen IMMER aus den Positionen
 *     (calculateQuoteTotals), nie aus der abgelesenen Endsumme. Eine
 *     abgelesene Summe kann falsch erkannt sein; eine Angebotssumme, die
 *     nicht zu ihren Positionen passt, ist in jeder Auswertung Gift.
 *  2. Die abgelesene Summe geht trotzdem nicht verloren: sie wird als
 *     `statedNet`/`statedGross` aufbewahrt und die Abweichung ausgewiesen,
 *     damit der Nutzer vor dem Anlegen sieht, ob die Positionen vollstaendig
 *     erkannt wurden.
 *
 * Rein und ohne React/Supabase.
 */

/** Kopfdaten, wie sie aus dem Beleg kommen. Alle Felder optional. */
export interface QuoteDocumentHeader {
    originalNumber: string;
    quoteDate: string;
    validUntil: string;
    title: string;
    customerName: string;
    siteStreet: string;
    siteZip: string;
    siteCity: string;
    taxRate: number;
    discountPercent: number;
    statedNet: number;
    statedGross: number;
}

export interface QuoteDocumentImport {
    header: QuoteDocumentHeader;
    /** Positionen in Dokumentreihenfolge, Sektionen bereits aufgeloest. */
    items: QuoteItem[];
    /** Aus den Abschnitten des Belegs gebildete Sektionen, in Reihenfolge. */
    sections: QuoteSection[];
    /** Zeilen, die der Beleg enthielt, die aber keine Leistung sind. */
    skipped: { bezeichnung: string; grund: string }[];
    /** Netto aus den erkannten Positionen (inkl. Gesamtnachlass). */
    computedNet: number;
    /**
     * computedNet - statedNet. 0 wenn der Beleg keine Summe ausweist.
     * Ein Betrag ungleich 0 heisst: es fehlen Positionen oder ein Preis wurde
     * falsch gelesen — der Import zeigt das an, statt es zu gluetten.
     */
    netDeviation: number;
}

const str = (v: unknown): string => (v === null || v === undefined ? '' : String(v)).trim();

const num = (v: unknown): number => {
    if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
    if (typeof v === 'string') {
        const n = parseDecimalInput(v);
        return n !== null && Number.isFinite(n) ? n : 0;
    }
    return 0;
};

/** Nur Datumsangaben im ISO-Format uebernehmen — geraten wird nichts. */
const isoDate = (v: unknown): string => {
    const s = str(v);
    return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : '';
};

const bool = (v: unknown): boolean => v === true || v === 'true';

/**
 * Einheitspreis einer Belegzeile. Steht kein EP im Dokument, aber ein
 * Gesamtpreis und eine Menge, wird er zurueckgerechnet — sonst stuende die
 * Position mit 0 im Angebot und die Summe waere still zu niedrig.
 */
export const resolveUnitPriceFromRow = (
    unitPrice: number,
    lineTotal: number,
    quantity: number,
): number => {
    if (unitPrice > 0) return round2(unitPrice);
    if (lineTotal > 0 && quantity > 0) return round2(lineTotal / quantity);
    return 0;
};

/**
 * KI-Antwort des Belegs -> Angebotsentwurf.
 *
 * `parsed` ist das Objekt aus geminiService.extractQuoteDocument
 * ({ angebot, positionen }). Fehlende oder unbrauchbare Felder fuehren zu
 * leeren Werten, nie zu geratenen.
 */
export const parseQuoteDocument = (parsed: unknown): QuoteDocumentImport => {
    const root = (parsed && typeof parsed === 'object' && !Array.isArray(parsed))
        ? parsed as Record<string, unknown>
        : {};
    const head = (root.angebot && typeof root.angebot === 'object')
        ? root.angebot as Record<string, unknown>
        : {};

    const taxRate = num(head.steuersatz);
    const discountPercent = num(head.nachlass_prozent);

    const header: QuoteDocumentHeader = {
        originalNumber: str(head.nummer),
        quoteDate: isoDate(head.datum),
        validUntil: isoDate(head.gueltig_bis),
        title: str(head.titel),
        customerName: str(head.kunde),
        siteStreet: str(head.strasse),
        siteZip: str(head.plz),
        siteCity: str(head.ort),
        // Ein Beleg ohne erkennbaren Satz bekommt keinen erfundenen — der
        // Aufrufer setzt dann den Hausstandard.
        taxRate: taxRate > 0 && taxRate <= 100 ? taxRate : 0,
        discountPercent: discountPercent > 0 && discountPercent < 100 ? discountPercent : 0,
        statedNet: num(head.netto),
        statedGross: num(head.brutto),
    };

    const rawRows = Array.isArray(root.positionen) ? root.positionen : [];
    const items: QuoteItem[] = [];
    const skipped: { bezeichnung: string; grund: string }[] = [];
    const sections: QuoteSection[] = [];
    const sectionByTitle = new Map<string, string>();
    // Wie beim LV-Import: die KI liefert in Dokumentreihenfolge, also gilt der
    // zuletzt gesehene Titel fuer alle folgenden Zeilen ohne eigenen.
    let currentSection = '';

    const sectionIdFor = (title: string): string | undefined => {
        const t = title.trim();
        if (!t) return undefined;
        const key = t.toLowerCase();
        let id = sectionByTitle.get(key);
        if (!id) {
            id = crypto.randomUUID();
            sectionByTitle.set(key, id);
            sections.push({ id, title: t });
        }
        return id;
    };

    for (const raw of rawRows) {
        const r = (raw ?? {}) as Record<string, unknown>;
        const bezeichnung = str(r.bezeichnung);
        const einheit = str(r.einheit);
        const menge = parseTenderQuantity(r.menge);
        const abschnitt = str(r.abschnitt);

        // Dieselbe Einstufung wie beim LV-Import: Titel- und Summenzeilen
        // gehoeren nicht in die Positionsliste, sonst verschieben sie jede
        // folgende Positionsnummer.
        const { kind, note } = classifyTenderLvRow({ bezeichnung, einheit, menge });
        if (kind === 'heading') {
            currentSection = abschnitt || bezeichnung || currentSection;
            skipped.push({ bezeichnung, grund: note });
            continue;
        }
        if (abschnitt) currentSection = abschnitt;
        if (kind !== 'service') {
            skipped.push({ bezeichnung: bezeichnung || '—', grund: note });
            continue;
        }

        const unitPrice = resolveUnitPriceFromRow(
            num(r.einheitspreis),
            num(r.gesamtpreis),
            menge,
        );

        items.push({
            id: crypto.randomUUID(),
            lvPosition: str(r.position),
            title: bezeichnung,
            unit: einheit,
            quantity: menge,
            unitPrice,
            discountPercent: 0,
            sectionId: sectionIdFor(currentSection),
            nep: bool(r.eventualposition) || undefined,
        });
    }

    const totals = calculateQuoteTotals(items, header.discountPercent, header.taxRate);
    const computedNet = totals.netTotal;

    return {
        header,
        items,
        sections,
        skipped,
        computedNet,
        netDeviation: header.statedNet > 0 ? round2(computedNet - header.statedNet) : 0,
    };
};

// ─── Anlegen des Angebotsdatensatzes ────────────────────────────────────────

/** Vom Nutzer im Review bestaetigte bzw. korrigierte Angaben. */
export interface QuoteImportChoice {
    parsed: QuoteDocumentImport;
    status: QuoteStatus;
    title: string;
    /** ISO-Datum. Leer = der Beleg trug keins. */
    quoteDate: string;
    validUntil: string;
    /** ISO-Datum der Entscheidung; nur bei 'accepted'/'rejected' relevant. */
    decisionDate: string;
    taxRate: number;
    fileName: string;
}

/**
 * Feldwerte fuer das frisch angelegte Angebot.
 *
 * Zu den Datumsankern: die Angebots-Statistik verankert Flussgroessen am
 * ENTSCHEIDUNGSDATUM und faellt ohne eins auf `updatedAt` zurueck. Bei einem
 * eingelesenen Altbeleg waere das der Importzeitpunkt — ein Angebot von 2024
 * landete damit im laufenden Monat und blaehte die aktuelle Erfolgsquote auf.
 * Deshalb werden die Anker hier explizit gesetzt: Versand am Angebotsdatum,
 * Entscheidung am (im Review bestaetigten) Entscheidungsdatum.
 */
export const buildImportedQuoteUpdates = (
    choice: QuoteImportChoice,
    nowIso: string,
    baseDetails: QuoteDetails = {},
): Partial<Quote> => {
    const { parsed } = choice;
    const totals = calculateQuoteTotals(parsed.items, parsed.header.discountPercent, choice.taxRate);
    // Fallbackkette der Datumsanker: bestaetigte Entscheidung -> Angebotsdatum
    // -> Importzeitpunkt. Nur der letzte Fall ist geraten, und nur dann, wenn
    // der Beleg ueberhaupt kein Datum trug.
    const anchor = choice.quoteDate || nowIso.slice(0, 10);
    const decision = choice.decisionDate || anchor;

    const updates: Partial<Quote> = {
        status: choice.status,
        title: choice.title,
        quoteDate: anchor,
        taxRate: choice.taxRate,
        discountPercent: parsed.header.discountPercent,
        items: parsed.items,
        netTotal: totals.netTotal,
        grossTotal: totals.grossTotal,
        details: {
            ...baseDetails,
            sections: parsed.sections,
            ...(parsed.header.siteStreet ? { siteStreet: parsed.header.siteStreet } : {}),
            ...(parsed.header.siteZip ? { siteZip: parsed.header.siteZip } : {}),
            ...(parsed.header.siteCity ? { siteCity: parsed.header.siteCity } : {}),
            importedQuote: {
                source: 'pdf',
                fileName: choice.fileName || undefined,
                importedAt: nowIso,
                originalNumber: parsed.header.originalNumber || undefined,
                statedNet: parsed.header.statedNet || undefined,
                statedGross: parsed.header.statedGross || undefined,
            },
        },
    };
    if (choice.validUntil) updates.validUntil = choice.validUntil;
    // Ein entschiedenes Angebot war zwangslaeufig auch versendet.
    if (choice.status !== 'draft') updates.sentAt = anchor;
    if (choice.status === 'accepted') updates.acceptedAt = decision;
    if (choice.status === 'rejected') updates.rejectedAt = decision;
    return updates;
};

/** Ist dieses Angebot ein eingelesener Altbeleg? */
export const isImportedQuote = (q: { details?: QuoteDetails }): boolean =>
    !!q.details?.importedQuote;
